import { DefaultLanguage } from '../i18n/config';

export interface FormatBusinessDateOptions {
  /**
   * Locale (`en-US`, `es-MX`, `fr-CA`). When omitted, falls back to the
   * configured default.
   */
  locale?: string;
  /**
   * Override the date-style preset. Defaults to `medium` for a `May 15, 2026`
   * shape; callers can pass `'long'` for `May 15, 2026` (US) / `15 mai 2026`
   * (FR) variants, etc.
   */
  dateStyle?: Intl.DateTimeFormatOptions['dateStyle'];
}

/**
 * Parses an ISO date string (`YYYY-MM-DD`) or `Date` value as a *calendar
 * date* with no time component. Returns a `Date` whose `getUTCFullYear()`,
 * `getUTCMonth()`, `getUTCDate()` match the input components exactly so
 * `Intl.DateTimeFormat` with `timeZone: 'UTC'` reproduces the same calendar
 * date regardless of viewer time zone.
 *
 * Per [architecture §5.9.3](../../../../docs/shared/architecture.md), business
 * dates (billing period start, request due-date, line-of-service activation)
 * must not shift across viewers. The contract: the back end serializes them
 * as date-only ISO strings (`YYYY-MM-DD`); the SPA renders them with UTC as
 * the formatting time zone so all viewers see the same calendar day.
 */
function parseBusinessDate(value: Date | string): Date {
  if (value instanceof Date) {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }

  // Match `YYYY-MM-DD` (with optional time we strip). Anything else falls
  // back to `Date.parse`, but business dates from the API are date-only by
  // contract.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  }

  const parsed = new Date(value);
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

/**
 * Formats a calendar date with **no** time-zone conversion. The output is
 * identical for a viewer in Tokyo and a viewer in Los Angeles - both see the
 * same calendar day. Use for billing periods, request due dates, line-of-
 * service activation dates, and any other date-only business value.
 */
export function formatBusinessDate(
  value: Date | string,
  opts: FormatBusinessDateOptions = {},
): string {
  const date = parseBusinessDate(value);

  return new Intl.DateTimeFormat(opts.locale ?? DefaultLanguage, {
    dateStyle: opts.dateStyle ?? 'medium',
    timeZone: 'UTC',
  }).format(date);
}
